// The authorization code flow's pure parts (02-DESIGN/01-identity/03-signing-in.md
// § the browser): a PKCE verifier and its S256 challenge, and the `state` the
// page checks on return and the `nonce` the ID token must echo. Everything
// here is RFC 7636 and OpenID Connect Core as written; nothing is Impire's own.

/** base64url encodes bytes without padding, as RFC 7636 § 4.1 specifies. */
export function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) {
    s += String.fromCharCode(b);
  }
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** randomBytes fills n bytes from the platform's generator. */
export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

/** VERIFIER_BYTES is the randomness a verifier carries: 32 bytes, 43 characters encoded. */
export const VERIFIER_BYTES = 32;

/** The alphabet RFC 7636 admits for a verifier: unreserved characters, 43 to 128 of them. */
export const VERIFIER_PATTERN = /^[A-Za-z0-9\-._~]{43,128}$/;

/** newVerifier mints a code verifier. */
export function newVerifier(): string {
  return base64url(randomBytes(VERIFIER_BYTES));
}

/** challenge computes the S256 challenge of a verifier. */
export async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** TOKEN_BYTES is the randomness a state or nonce carries. */
export const TOKEN_BYTES = 24;

/** newState mints the `state` the page checks on return. */
export function newState(): string {
  return base64url(randomBytes(TOKEN_BYTES));
}

/** newNonce mints the `nonce` the ID token must echo. */
export function newNonce(): string {
  return base64url(randomBytes(TOKEN_BYTES));
}
